/* eslint-disable import/no-unresolved, import/no-extraneous-dependencies, no-return-await */
import React from 'react';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { useQuery } from '@tanstack/react-query';
import { Navigate, NavLink, useParams } from 'react-router-dom';
import { OmniverseHeader, LoaderError } from '../components';
import { useOmniverseConfig } from '../hooks/useConfig';
import { getStreamingApp } from '../state/Apps';

export default function AppInfo() {
  const config = useOmniverseConfig();
  const { appId = '' } = useParams<{ appId: string }>();

  const { isLoading, data, error } = useQuery({
    queryKey: ['streaming-app', appId],
    queryFn: async () =>
      await getStreamingApp({
        appId,
        config,
      }),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <OmniverseHeader />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <OmniverseHeader />
        <LoaderError title={'Failed to load application info'}>
          {error.message}
        </LoaderError>
      </Box>
    );
  }

  if (!data) {
    return <Navigate to={'/omniverse'} />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <OmniverseHeader />
      <Box sx={{ px: 6, py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box
            component="img"
            src={data.icon}
            alt={data.title}
            sx={{ height: 64 }}
          />
          <Typography variant="h4" sx={{ color: 'text.secondary' }}>
            {data.title}
          </Typography>
        </Box>

        {/* <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={NavLink} to="/omniverse" underline="hover">
            Main page
          </Link>
          <Link component={NavLink} to="/omniverse/sessions" underline="hover">
            Sessions
          </Link>
          <Link
            component={NavLink}
            to={`/omniverse/app/${appId}`}
            underline="hover"
          >
            {data.title}
          </Link>
        </Breadcrumbs> */}

        <TableContainer
          component={Paper}
          sx={{
            boxShadow: '3px 3px 5px #E8E8E8',
            border: '0.1rem solid #E8E8E8',
          }}
        >
          <Table>
            <TableBody>
              <TableRow>
                <TableCell
                  component="th"
                  scope="row"
                  sx={{ width: 150, fontWeight: 600 }}
                >
                  Version
                </TableCell>
                <TableCell>{data.latestVersion.name}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                  Category
                </TableCell>
                <TableCell>{data.category}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                  Product area
                </TableCell>
                <TableCell>{data.productArea}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                  Function ID
                </TableCell>
                <TableCell>{data.latestVersion.functionId}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                  Function version
                </TableCell>
                <TableCell>{data.latestVersion.functionVersionId}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                  Authentication type
                </TableCell>
                <TableCell>{data.authType ?? 'NONE'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
